import React, { useState } from "react";
import { DollarSign, FileText } from "lucide-react";

/**
 * Reusable tender capture form (Cash | Check + amount + optional check #).
 * Used by TicketModal (single funds-received) and DepositTab (bulk funds-received).
 * Returns: { tender_type, tender_amount, check_number } via onSubmit.
 */
export const TenderForm = ({ defaultAmount = 100, defaultType = "cash", onSubmit, onCancel, busy, applyLabel = "Confirm" }) => {
  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState(String(defaultAmount));
  const [checkNo, setCheckNo] = useState("");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e?.preventDefault?.();
    setErr("");
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) { setErr("Amount must be greater than 0"); return; }
    if (type === "check" && !checkNo.trim()) { setErr("Check number is required for check tender"); return; }
    onSubmit({
      tender_type: type,
      tender_amount: amt,
      check_number: type === "check" ? checkNo.trim() : "",
    });
  };

  const labelStyle = { color: "var(--ink-tertiary)" };
  const inputStyle = { background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-primary)" };

  const typeBtn = (active, activeColor) => ({
    background: active ? `color-mix(in oklab, ${activeColor} 12%, var(--surface-card))` : "var(--surface-card)",
    borderColor: active ? activeColor : "var(--border-default)",
    color: active ? activeColor : "var(--ink-secondary)",
    boxShadow: active ? `inset 0 0 0 1px ${activeColor}` : "none",
  });

  return (
    <div data-testid="tender-form" className="space-y-3">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={labelStyle}>Payment type</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="tender-cash"
            onClick={() => setType("cash")}
            aria-pressed={type === "cash"}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors"
            style={typeBtn(type === "cash", "var(--semantic-success)")}
          >
            <DollarSign className="h-4 w-4" /> Cash
          </button>
          <button
            type="button"
            data-testid="tender-check"
            onClick={() => setType("check")}
            aria-pressed={type === "check"}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors"
            style={typeBtn(type === "check", "var(--brand-primary)")}
          >
            <FileText className="h-4 w-4" /> Check
          </button>
        </div>
      </div>

      <div className={`grid gap-2 ${type === "check" ? "grid-cols-2" : "grid-cols-1"}`}>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={labelStyle}>Amount ($)</label>
          <input
            data-testid="tender-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
            style={inputStyle}
          />
        </div>
        {type === "check" && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide mb-1.5 block" style={labelStyle}>Check number</label>
            <input
              data-testid="tender-check-number"
              value={checkNo}
              onChange={(e) => setCheckNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder="e.g. 1234"
              className="w-full rounded-md border px-3 py-2 text-base focus:outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
              style={inputStyle}
            />
          </div>
        )}
      </div>

      {err && <p className="text-sm font-medium" style={{ color: "var(--semantic-danger)" }}>{err}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="tender-submit"
          className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-50"
          style={{ background: "var(--brand-primary)", color: "var(--ink-on-brand)" }}
        >
          {busy ? "Saving…" : applyLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="tender-cancel"
            className="px-4 py-2.5 rounded-md border text-sm font-semibold disabled:opacity-50"
            style={{ background: "var(--surface-card)", borderColor: "var(--border-default)", color: "var(--ink-secondary)" }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};
